// @ts-nocheck
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { EMPTY_OBJECT, EMPTY_ARRAY } from '../const/CommonConst';
import { getVaultBaseUrl } from '../utils/apiHostOverride';

export const BASE_URL = `${getVaultBaseUrl()}/vault`;

/**
 * Custom query engine implementing subscribed-params semantics.
 *
 * - Hook `params` are the initial/default subscribed params for `data`, `refetch`, and loading state.
 * - `execute({ params })` is a subscribe-and-fetch operation: runtime params become the hook's active subscribed params and drive the state.
 * - `execute()` or `execute({})` fetches the current active subscribed params.
 * - `execute({ force: true })` force-fetches the current active subscribed params bypassing staleTime.
 * - `execute({ params, force: true })` switches the active subscribed params and force-fetches them.
 * - Avoid passing different runtime params repeatedly to the same `execute` reference.
 * - Avoid passing params in both hook args and inside execute.
 *
 * @param {Function} rawQueryKeyFn - Function that returns the query key array based on params.
 * @param {Function} rawFiringFn - Function that returns the promise for the API call based on params and loader flag.
 * @param {Object} [params] - The initial/default parameters to subscribe to.
 * @param {Object} [options] - Additional react-query options, including a custom `loader` boolean flag (default true).
 */
export const useNewQueryEngine = (
  rawQueryKeyFn,
  rawFiringFn,
  params = EMPTY_OBJECT,
  options = EMPTY_OBJECT
) => {
  const latestRef = useRef(null);
  const queryClient = useQueryClient();

  const [runtimeParams, setRuntimeParams] = useState();
  const activeParams = runtimeParams ?? params;

  const { loader = true, select, ...queryOptions } = options;

  const defaultOptions = {
    enabled: true,
    retry: 1,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    select,
    ...queryOptions,
  };

  const query = useQuery({
    queryKey: rawQueryKeyFn(activeParams),
    queryFn: () => rawFiringFn(activeParams, loader).then((res) => res.data),
    ...defaultOptions,
  });

  /**
   * whenever component re-renders these values/props get a new reference,
   * if we put them inside execute function, it will create a new function reference on every render,
   * which will cause unnecessary re-renders in child components where this function is in deps
   * so we store them in a ref to always have the latest values for execute function
   * now whenever execute function is called, it will always have the latest values from the ref, without causing re-renders
   */
  latestRef.current = {
    activeParams,
    loader,
    select,
    defaultOptions,
    rawQueryKeyFn,
    rawFiringFn,
  };

  /**
   * execute({ params, force })
   *
   * @param {Object} [args.params] - New parameters to subscribe to and fetch. If provided, switches the hook's active subscribed params.
   * @param {boolean} [args.force=false] - If true, fetches fresh data bypassing the staleTime (staleTime: 0).
   */
  const execute = useCallback(
    ({ params: newParams, force = false } = EMPTY_OBJECT) => {
      const ref = latestRef.current;

      let finalParams = ref.activeParams;

      if (newParams !== undefined) {
        finalParams = newParams;
        setRuntimeParams(newParams);
      }

      return queryClient
        .fetchQuery({
          queryKey: ref.rawQueryKeyFn(finalParams),
          queryFn: () =>
            ref.rawFiringFn(finalParams, ref.loader).then((res) => res.data),
          retry: ref.defaultOptions.retry,
          retryDelay: ref.defaultOptions.retryDelay,
          staleTime: force ? 0 : ref.defaultOptions.staleTime,
          gcTime: ref.defaultOptions.gcTime,
        })
        .then((data) => (ref.select ? ref.select(data) : data));
    },
    [queryClient]
  );

  return { ...query, execute };
};

export const useQueryEngine = (queryKey, queryFn, options = EMPTY_OBJECT) => {
  // Default options that align with project standards
  const defaultOptions = {
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s... capped at 30s
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  };
  // useQuery expects an object with queryKey and queryFn, plus any additional options
  const query = useQuery({
    queryKey,
    queryFn,
    ...defaultOptions,
  });

  return query;
};

export const useMutationEngine = (mutationFn, options = EMPTY_OBJECT) => {
  const queryClient = useQueryClient();
  const {
    invalidateQueries = EMPTY_ARRAY,
    onSuccess,
    ...mutationOptions
  } = options;

  const defaultOptions = {
    retry: 1, // Retry once on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff delay with 30s maximum
    ...mutationOptions,
  };

  return useMutation({
    mutationFn: (...args) => mutationFn(...args).then((res) => res.data),
    ...defaultOptions,
    onSuccess: async (...args) => {
      if (Array.isArray(invalidateQueries) && invalidateQueries.length > 0) {
        await Promise.all(
          invalidateQueries.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey })
          )
        );
      }

      await onSuccess?.(...args);
    },
  });
};
